import { message } from 'antd';
import { CustomSessionRuntimeData } from '../../custom_shared/types';
import { WorksheetModel } from '../models';
import { AccountingStatementService } from '../services';
import { TypeUtils } from '../../common/misc';
import { DateTime } from 'luxon';
import SessionStore from './SessionStore';
import WorksheetsStore from './WorksheetsStore';
import InputsStore from './InputsStore';

const Actions = {
    AddEntryCode: 'AddEntryCode',
    RemoveEntryCode: 'RemoveEntryCode',
    AddBalanceInformation: 'AddBalanceInformation',
    ChangeBusinessId: 'ChangeBusinessId',
    ChangeInsuredPeriodStart: 'ChangeInsuredPeriodStart',
    ChangeSignedShareType: 'ChangeSignedShareType',
    ChangeUnderwritingYear: 'ChangeUnderwritingYear',
    ChangeDocumentSignedShare: 'ChangeDocumentSignedShare',
    ProcessWorksheets: 'ProcessWorksheets'
} as const;

const ActionParams = {
    SelectedWorksheetIds: 'SelectedWorksheetIds',
    BalanceInformationNodeId: 'BalanceInformationNodeId',
    EntryCodeNodeId: 'EntryCodeNodeId',
    Metadata: 'Metadata'
} as const;

type ExecuteActionParams = {
    action: string;
    actionParams: Record<string, unknown>;
};

export default class ActionStore {
    static RuntimeDataKeys = {
        Worksheets: 'Worksheets',
        ActionParams: 'ActionParams'
    };

    constructor(
        private readonly sessionStore: SessionStore,
        private readonly worksheetsStore: WorksheetsStore,
        private readonly inputsStore: InputsStore,
        private readonly accountingStatementService: AccountingStatementService
    ) {}

    async addEntryCode(selectedWorksheetId: string, balanceInformationNodeId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId
        };

        return this.executeAction({ action: Actions.AddEntryCode, actionParams });
    }

    async removeEntryCode(selectedWorksheetId: string, balanceInformationNodeId: string, entryCodeNodeId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId,
            [ActionParams.EntryCodeNodeId]: entryCodeNodeId
        };

        return this.executeAction({ action: Actions.RemoveEntryCode, actionParams });
    }

    async addBalanceInformation(selectedWorksheetId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId]
        };

        return this.executeAction({ action: Actions.AddBalanceInformation, actionParams });
    }

    async changeBusinessId(
        selectedWorksheetId: string,
        balanceInformationNodeId: string,
        metadata: Record<string, unknown> = {}
    ) {
        const actionParams: Record<string, unknown> = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId,
            [ActionParams.Metadata]: metadata
        };

        return this.executeAction({ action: Actions.ChangeBusinessId, actionParams });
    }

    async changeInsuredPeriodStart(selectedWorksheetId: string, balanceInformationNodeId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId
        };

        return this.executeAction({ action: Actions.ChangeInsuredPeriodStart, actionParams });
    }

    async changeSignedShareType(selectedWorksheetId: string, balanceInformationNodeId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId
        };

        return this.executeAction({ action: Actions.ChangeSignedShareType, actionParams });
    }

    async changeUnderwritingYear(selectedWorksheetId: string, balanceInformationNodeId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId
        };

        return this.executeAction({ action: Actions.ChangeUnderwritingYear, actionParams });
    }

    async changeDocumentSignedShare(selectedWorksheetId: string, balanceInformationNodeId: string) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: [selectedWorksheetId],
            [ActionParams.BalanceInformationNodeId]: balanceInformationNodeId
        };

        return this.executeAction({ action: Actions.ChangeDocumentSignedShare, actionParams });
    }

    async processWorksheets(selectedWorksheetIds: string[]) {
        const actionParams = {
            [ActionParams.SelectedWorksheetIds]: selectedWorksheetIds
        };

        return this.executeAction({ action: Actions.ProcessWorksheets, actionParams });
    }

    async downloadJson() {
        try {
            const response = await this.accountingStatementService.downloadJson(
                this.sessionStore.sessionExtension,
                this.sessionStore.sessionId,
                this.worksheetsStore.worksheets.map(w => w.dto)
            );

            if (response.isErr()) {
                message.error('Failed to download accounting statement JSON');
                console.error(response.error.text);
                return;
            }

            response.map(blob => {
                // Create a download link and trigger the download
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const formattedDate = DateTime.now().toFormat('dd_MM_yyyy_HH_mm_ss');
                a.download = `${formattedDate}_AccountingStatementPayload_${this.sessionStore.sessionId}.json`;

                document.body.appendChild(a);
                a.click();

                // Cleanup
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            });
        } catch (error) {
            console.error('Failed to download accounting statement JSON', error);
        }
    }

    private async executeAction({ action, actionParams }: ExecuteActionParams): Promise<boolean> {
        // Make sure any recent edits are pushed before the action runs.
        await this.inputsStore.flushPendingInputChanges();

        const selectedWorksheetIdsParam = actionParams[ActionParams.SelectedWorksheetIds];
        const selectedWorksheetIds = TypeUtils.isStringArray(selectedWorksheetIdsParam)
            ? selectedWorksheetIdsParam
            : [];

        // Send all worksheets that are selected or have changed data to prevent large payloads and loss of data
        const worksheets = this.worksheetsStore.worksheets.filter(
            w => selectedWorksheetIds.includes(w.id) || w.hasChangedData
        );

        await this.sessionStore.continueSession(action, {
            ...this.getWorksheetsRuntimeData(worksheets),
            [ActionStore.RuntimeDataKeys.ActionParams]: { value: actionParams }
        });

        return true;
    }

    private getWorksheetsRuntimeData(worksheets: WorksheetModel[]): CustomSessionRuntimeData {
        return {
            [ActionStore.RuntimeDataKeys.Worksheets]: {
                value: JSON.stringify(worksheets.map(w => w.dto))
            }
        };
    }
}
